import { useMemo } from "react";
import type {
  PatternTrainProps,
  PatternColor,
  PatternShape,
  PatternElement,
} from "./types";
import { PATTERN_COLORS, PATTERN_SHAPES } from "./types";

/**
 * PatternTrain Component
 *
 * A whimsical train-themed pattern game where children complete
 * the pattern to help Prism's rainbow train chug along! Features
 * adorable train cars carrying pattern elements with delightful
 * animations when completed.
 *
 * @example
 * <PatternTrain
 *   pattern={['red', 'blue', 'red', 'blue', 'red']}
 *   missingIndex={4}
 *   options={['red', 'blue', 'yellow']}
 *   correctAnswer="red"
 *   patternType="color"
 *   onAnswer={handleAnswer}
 *   selectedAnswer={selected}
 * />
 */
export function PatternTrain({
  pattern,
  missingIndex,
  options,
  correctAnswer,
  onAnswer,
  selectedAnswer,
  isSubmitted = false,
  isCorrect = false,
  patternType,
  discipline = "patterns",
}: PatternTrainProps) {
  // Check element type
  const isColor = (element: PatternElement): element is PatternColor => {
    return typeof element === "string" && element in PATTERN_COLORS;
  };

  const isShape = (element: PatternElement): element is PatternShape => {
    return typeof element === "string" && element in PATTERN_SHAPES;
  };

  // Render the cargo content of a train car
  const renderCargo = (
    element: PatternElement,
    isMissing: boolean,
    size: "sm" | "md" = "md"
  ) => {
    const sizeClasses = {
      sm: "w-8 h-8 md:w-10 md:h-10",
      md: "w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14",
    };

    if (isMissing) {
      if (selectedAnswer) {
        if (isColor(selectedAnswer)) {
          return (
            <div
              className={`
                ${sizeClasses[size]}
                rounded-lg
                ${PATTERN_COLORS[selectedAnswer].bg}
                shadow-[0_2px_8px_rgba(0,0,0,0.15)]
                ${isSubmitted && isCorrect ? "animate-celebrate" : "animate-star-pop"}
              `}
            />
          );
        }
        if (isShape(selectedAnswer)) {
          return (
            <span
              className={`
                ${size === "sm" ? "text-2xl" : "text-3xl md:text-4xl"}
                drop-shadow-md
                ${isSubmitted && isCorrect ? "animate-celebrate" : "animate-star-pop"}
              `}
            >
              {PATTERN_SHAPES[selectedAnswer].emoji}
            </span>
          );
        }
      }
      return (
        <span className="text-2xl md:text-3xl text-neutral-400 animate-pulse font-display">
          ?
        </span>
      );
    }

    if (isColor(element)) {
      return (
        <div
          className={`
            ${sizeClasses[size]}
            rounded-lg
            ${PATTERN_COLORS[element].bg}
            shadow-[0_2px_8px_rgba(0,0,0,0.15)]
          `}
        />
      );
    }

    if (isShape(element)) {
      return (
        <span
          className={`
            ${size === "sm" ? "text-2xl" : "text-3xl md:text-4xl"}
            drop-shadow-md
          `}
        >
          {PATTERN_SHAPES[element].emoji}
        </span>
      );
    }

    return (
      <span className="font-bold text-lg text-neutral-600">{element}</span>
    );
  };

  return (
    <div className="flex flex-col items-center gap-8 w-full">
      {/* Train Track Area */}
      <div
        className="
          relative w-full
          bg-gradient-to-b from-[#87CEEB] via-[#B0E0E6] to-[#E0F4FF]
          rounded-3xl p-6 md:p-8 lg:p-10
          border-4 border-[#6BB3D9]
          shadow-[0_8px_40px_rgba(107,179,217,0.2),inset_0_2px_0_rgba(255,255,255,0.8)]
          overflow-hidden
        "
      >
        {/* Sky decorations */}
        <div className="absolute top-3 left-6 text-3xl opacity-80">☀️</div>
        <div
          className="absolute top-8 right-8 text-xl opacity-60 animate-float"
          style={{ animationDelay: "0s" }}
        >
          ☁️
        </div>
        <div
          className="absolute top-4 right-24 text-lg opacity-40 animate-float"
          style={{ animationDelay: "1s" }}
        >
          ☁️
        </div>
        <div
          className="absolute top-12 left-20 text-sm opacity-30 animate-float"
          style={{ animationDelay: "1.5s" }}
        >
          🦋
        </div>

        {/* Rainbow arc decoration */}
        <div
          className="
            absolute -top-20 left-1/2 -translate-x-1/2
            w-80 h-40
            rounded-t-full
            bg-gradient-to-r from-prism-red/30 via-prism-yellow/30 via-prism-green/30 via-prism-blue/30 to-prism-purple/30
            opacity-60
          "
        />

        {/* Train Track */}
        <div
          className="
            absolute bottom-8 left-0 right-0
            h-4
            bg-gradient-to-r from-[#8B6914] via-[#A67B14] to-[#8B6914]
            border-t-2 border-b-2 border-[#6B4914]
          "
        >
          {/* Track ties */}
          <div className="absolute inset-0 flex justify-evenly items-center">
            {Array.from({ length: 20 }).map((_, i) => (
              <div
                key={i}
                className="w-2 h-6 bg-[#6B4914] rounded-sm -mt-1"
              />
            ))}
          </div>
        </div>

        {/* Train Cars */}
        <div
          className="
            relative
            flex items-end justify-center gap-1 md:gap-2
            pb-10
          "
        >
          {/* Engine */}
          <div
            className="
              relative
              w-16 h-20 md:w-20 md:h-24 lg:w-24 lg:h-28
              flex flex-col items-center justify-end
            "
            style={{
              animation: isSubmitted && isCorrect
                ? "train-chug 0.5s ease-in-out 3"
                : "train-idle 2s ease-in-out infinite",
            }}
          >
            {/* Smokestack */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <div className="w-4 h-6 bg-gradient-to-b from-[#4A4A4A] to-[#2A2A2A] rounded-t-lg" />
              {/* Steam puffs */}
              {isSubmitted && isCorrect && (
                <>
                  <span
                    className="absolute -top-3 left-1/2 -translate-x-1/2 text-lg animate-float opacity-70"
                    style={{ animationDelay: "0s" }}
                  >
                    💨
                  </span>
                  <span
                    className="absolute -top-6 left-1/2 translate-x-1 text-sm animate-float opacity-50"
                    style={{ animationDelay: "0.3s" }}
                  >
                    💨
                  </span>
                </>
              )}
            </div>

            {/* Engine body */}
            <div
              className="
                w-full h-14 md:h-16 lg:h-20
                bg-gradient-to-b from-[#E55555] via-[#D44444] to-[#B33333]
                rounded-t-2xl
                border-4 border-[#A22222]
                shadow-[0_4px_0_0_#8B1111,0_6px_16px_rgba(0,0,0,0.2)]
              "
            >
              {/* Window */}
              <div
                className="
                  mx-auto mt-2
                  w-6 h-4 md:w-8 md:h-5
                  bg-gradient-to-b from-[#87CEEB] to-[#6BB3D9]
                  rounded-t-lg
                  border-2 border-[#A22222]
                "
              />
            </div>

            {/* Wheels */}
            <div className="absolute -bottom-2 left-1 w-5 h-5 bg-[#2A2A2A] rounded-full border-2 border-[#4A4A4A]" />
            <div className="absolute -bottom-2 right-1 w-5 h-5 bg-[#2A2A2A] rounded-full border-2 border-[#4A4A4A]" />
          </div>

          {/* Train Cars with Pattern Elements */}
          {pattern.map((element, index) => {
            const isMissing = index === missingIndex;

            return (
              <div
                key={`car-${index}`}
                className="
                  relative
                  w-14 h-16 md:w-18 md:h-20 lg:w-22 lg:h-24
                  flex flex-col items-center justify-end
                "
                style={{
                  animation: isSubmitted && isCorrect
                    ? `train-chug 0.5s ease-in-out ${0.1 * (index + 1)}s 3`
                    : `train-idle 2s ease-in-out ${0.1 * index}s infinite`,
                }}
              >
                {/* Connector */}
                <div
                  className="
                    absolute left-0 top-1/2 -translate-x-full -translate-y-1/2
                    w-2 h-2
                    bg-[#4A4A4A]
                    rounded-full
                  "
                />

                {/* Car body */}
                <div
                  className={`
                    relative
                    w-full h-12 md:h-14 lg:h-16
                    flex items-center justify-center
                    rounded-t-xl
                    border-4
                    shadow-[0_4px_0_0_rgba(0,0,0,0.15),0_6px_12px_rgba(0,0,0,0.1)]
                    transition-all duration-300
                    ${
                      isMissing
                        ? selectedAnswer && isSubmitted && isCorrect
                          ? "bg-meadow-100 border-meadow-500"
                          : selectedAnswer
                            ? "bg-prism-blue/10 border-prism-blue"
                            : "bg-white/90 border-neutral-300 border-dashed"
                        : "bg-white border-[#DEB887]"
                    }
                  `}
                >
                  {renderCargo(element, isMissing, "md")}

                  {/* Celebration sparkles on missing slot */}
                  {isMissing && isSubmitted && isCorrect && (
                    <>
                      <span
                        className="absolute -top-2 -left-1 text-sm animate-sparkle"
                        style={{ animationDelay: "0s" }}
                      >
                        ✨
                      </span>
                      <span
                        className="absolute -top-2 -right-1 text-sm animate-sparkle"
                        style={{ animationDelay: "0.2s" }}
                      >
                        🌟
                      </span>
                    </>
                  )}
                </div>

                {/* Wheels */}
                <div className="absolute -bottom-2 left-1 w-4 h-4 bg-[#2A2A2A] rounded-full border-2 border-[#4A4A4A]" />
                <div className="absolute -bottom-2 right-1 w-4 h-4 bg-[#2A2A2A] rounded-full border-2 border-[#4A4A4A]" />
              </div>
            );
          })}
        </div>

        {/* Ground grass */}
        <div
          className="
            absolute bottom-0 left-0 right-0
            h-6
            bg-gradient-to-t from-[#7CB342] via-[#8BC34A] to-[#9CCC65]
          "
        />
      </div>

      {/* Answer Options */}
      <div className="w-full">
        <h3 className="text-center font-display font-bold text-body-xl text-neutral-800 mb-5">
          🚂 What goes in the missing train car?
        </h3>

        <div className="flex flex-wrap justify-center gap-4 md:gap-5">
          {options.map((option, index) => {
            const isSelected = selectedAnswer === option;
            const isCorrectOption = option === correctAnswer;

            return (
              <button
                key={`option-${option}-${index}`}
                type="button"
                onClick={() => !isSubmitted && onAnswer(option)}
                disabled={isSubmitted}
                className={`
                  relative
                  w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28
                  flex items-center justify-center
                  rounded-2xl
                  border-4
                  transition-all duration-200 ease-bounce
                  focus:outline-none focus-visible:ring-4 focus-visible:ring-prism-blue/50
                  ${getOptionStyles(isSelected, isSubmitted, isCorrect, isCorrectOption)}
                `}
                style={{ animationDelay: `${index * 60}ms` }}
                aria-label={`Select ${option}`}
                aria-pressed={isSelected}
              >
                {isColor(option) ? (
                  <div
                    className={`
                      w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16
                      rounded-xl
                      ${PATTERN_COLORS[option].bg}
                      shadow-[0_3px_10px_rgba(0,0,0,0.15)]
                    `}
                  />
                ) : isShape(option) ? (
                  <span className="text-4xl md:text-5xl drop-shadow-md">
                    {PATTERN_SHAPES[option].emoji}
                  </span>
                ) : (
                  <span className="font-bold text-2xl text-neutral-600">
                    {option}
                  </span>
                )}

                {/* Selection ring */}
                {isSelected && !isSubmitted && (
                  <div
                    className="
                      absolute inset-0
                      rounded-2xl
                      border-4 border-prism-blue
                      pointer-events-none
                    "
                  />
                )}

                {/* Correct answer sparkle */}
                {isSubmitted && isCorrectOption && (
                  <span className="absolute -top-2 -right-2 text-xl animate-sparkle">
                    ✨
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom animation styles */}
      <style>{`
        @keyframes train-idle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-2px); }
        }
        @keyframes train-chug {
          0%, 100% { transform: translateX(0) translateY(0); }
          25% { transform: translateX(2px) translateY(-1px); }
          50% { transform: translateX(0) translateY(0); }
          75% { transform: translateX(-2px) translateY(-1px); }
        }
      `}</style>
    </div>
  );
}

/**
 * Get styles for option button based on state
 */
function getOptionStyles(
  isSelected: boolean,
  isSubmitted: boolean,
  isCorrect: boolean,
  isCorrectOption: boolean
): string {
  // After submission
  if (isSubmitted) {
    if (isCorrectOption) {
      return `
        bg-gradient-to-b from-meadow-200 via-meadow-300 to-meadow-400
        border-meadow-600
        shadow-[0_4px_0_0_theme(colors.meadow.700),0_8px_20px_rgba(40,167,69,0.25)]
        animate-celebrate
      `;
    }
    if (isSelected && !isCorrect) {
      return `
        bg-neutral-200 border-neutral-400
        opacity-50 translate-y-0.5
        shadow-[0_2px_0_0_theme(colors.neutral.400)]
      `;
    }
    return `
      bg-neutral-100 border-neutral-300
      opacity-35
      shadow-none
      cursor-default
    `;
  }

  // Selected but not submitted
  if (isSelected) {
    return `
      bg-gradient-to-b from-prism-blue/20 via-prism-blue/40 to-prism-blue/60
      border-prism-blue
      shadow-[0_4px_0_0_theme(colors.prism.blue.dark),0_8px_20px_rgba(116,185,255,0.3)]
      -translate-y-1.5 scale-[1.03]
    `;
  }

  // Default unselected state
  return `
    bg-white border-neutral-300
    shadow-[0_4px_0_0_theme(colors.neutral.300),0_6px_16px_rgba(0,0,0,0.06)]
    hover:border-prism-blue/70 hover:bg-prism-blue/5
    hover:shadow-[0_6px_0_0_rgba(116,185,255,0.3),0_10px_24px_rgba(116,185,255,0.15)]
    hover:-translate-y-1.5
    active:translate-y-0.5 active:shadow-[0_2px_0_0_theme(colors.neutral.300)]
    cursor-pointer
  `;
}

export default PatternTrain;
